import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import NavigationBar from '../layout/NavigationBar';
import Footer from '../layout/footer';

const WebStoreSettings = () => {
    const [loading, setLoading] = useState(false);
    const [businessId, setBusinessId] = useState('');
    const [logoUploadLoading, setLogoUploadLoading] = useState(false);

    const [bannerSlides, setBannerSlides] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [featuredProducts, setFeaturedProducts] = useState([]);
    const [availableCategories, setAvailableCategories] = useState([]);
    const [availableProducts, setAvailableProducts] = useState([]);
    const [fourColumnSection, setFourColumnSection] = useState([]);

    const [storeData, setStoreData] = useState({
        business_name: '',
        store_subdomain: '',
        store_status: 'inactive',
        store_settings: {
            logo_url: '',
            primary_color: '#007bff',
            secondary_color: '#6c757d',
            header_color: '#2c3e50',  // Add this
            footer_color: '#2c3e50',
            accent_color: '#030303ff',
            font_family: 'Arial, sans-serif',
            store_description: '',
            contact_email: '',
            contact_phone: '',
            social_links: {
                facebook: '',
                instagram: '',
                twitter: ''
            },
            seo_settings: {
                meta_title: '',
                meta_description: '',
                meta_keywords: ''
            },
            banner_section: {
                enabled: false,
                slides: []
            },
            category_section: {
                enabled: false,
                title: 'Shop by Categories',
                selected_categories: []
            },
            featured_section: {
                enabled: false,
                title: 'Featured Products',
                selected_products: []
            }
        }
    });

    useEffect(() => {
        const business_id = localStorage.getItem('business_id');
        if (business_id) {
            setBusinessId(business_id);
            fetchStoreInfo(business_id);
            fetchCategories();
            fetchProducts(business_id);
        }
    }, []);

    const updateFetchStoreInfo = (data) => {
        setStoreData(data.result);

        // Set banner slides
        if (data.result.store_settings.banner_section?.slides) {
            setBannerSlides(data.result.store_settings.banner_section.slides);
        }

        // Set selected categories
        if (data.result.store_settings.category_section?.selected_categories) {
            setSelectedCategories(data.result.store_settings.category_section.selected_categories);
        }

        // Set featured products
        if (data.result.store_settings.featured_section?.selected_products) {
            setFeaturedProducts(data.result.store_settings.featured_section.selected_products);
        }
        if (data.result.store_settings.four_column_section?.columns) {
            setFourColumnSection(data.result.store_settings.four_column_section.columns);
        }
    };

    const fetchStoreInfo = async (businessId) => {
        try {
            setLoading(true);
            const response = await fetch(`${process.env.REACT_APP_APIURL}business/store/info?business_id=${businessId}`);
            const data = await response.json();

            if (data.status === 'success') {
                // Use the updateFetchStoreInfo function to properly set all data
                updateFetchStoreInfo(data);
            } else {
                toast.error('Failed to load store information');
            }
        } catch (error) {
            console.error('Error fetching store info:', error);
            toast.error('Error loading store information');
        } finally {
            setLoading(false);
        }
    };

    const handleLogoUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);
        formData.append('reference', 'event');
        formData.append('user_id', businessId);

        try {
            setLogoUploadLoading(true);
            const response = await fetch(`${process.env.REACT_APP_APIURL}file/event/upload`, {
                method: 'POST',
                body: formData
            });

            const data = await response.json();

            if (data.status === 'success' && data.result.files.length > 0) {
                const fileUrl = data.result.files[0].file_url;
                handleInputChange(null, 'logo_url', fileUrl);
                toast.success('Logo uploaded successfully');
            } else {
                toast.error('Failed to upload logo');
            }
        } catch (error) {
            console.error('Error uploading logo:', error);
            toast.error('Error uploading logo');
        } finally {
            setLogoUploadLoading(false);
            // Clear the input
            event.target.value = '';
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_APIURL}category/list?front_category=true`);
            const data = await response.json();
            if (data.status === 'success') {
                setAvailableCategories(data.result);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    const fetchProducts = async (id) => {
        try {
            const [greetingRes, giftRes] = await Promise.all([
                fetch(`${process.env.REACT_APP_APIURL}greeting/list?business_id=${id}`),
                fetch(`${process.env.REACT_APP_APIURL}gift/list?business_id=${id}`)
            ]);

            const greetingData = await greetingRes.json();
            const giftData = await giftRes.json();

            const combinedProducts = [
                ...(giftData.status === 'success' ? giftData.result.map(item => ({ ...item, type: 'gift' })) : [])
            ];

            setAvailableProducts(combinedProducts);
        } catch (error) {
            console.error('Error fetching products:', error);
        }
    };

    const handleBannerUpload = async (event, slideIndex = null) => {
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);
        formData.append('reference', 'banner');
        formData.append('user_id', businessId);

        try {
            setLogoUploadLoading(true);
            const response = await fetch(`${process.env.REACT_APP_APIURL}file/event/upload`, {
                method: 'POST',
                body: formData
            });

            const data = await response.json();

            if (data.status === 'success' && data.result.files.length > 0) {
                const fileUrl = data.result.files[0].file_url;

                if (slideIndex !== null) {
                    // Update existing slide
                    const updatedSlides = [...bannerSlides];
                    updatedSlides[slideIndex].image_url = fileUrl;
                    setBannerSlides(updatedSlides);
                    handleInputChange('banner_section', 'slides', updatedSlides);
                } else {
                    // Add new slide
                    const newSlide = {
                        id: Date.now(),
                        image_url: fileUrl,
                        title: '',
                        subtitle: '',
                        button_text: '',
                        button_link: '',
                        active: true
                    };
                    const updatedSlides = [...bannerSlides, newSlide];
                    setBannerSlides(updatedSlides);
                    handleInputChange('banner_section', 'slides', updatedSlides);
                }

                toast.success('Banner image uploaded successfully');
            } else {
                toast.error('Failed to upload banner image');
            }
        } catch (error) {
            console.error('Error uploading banner:', error);
            toast.error('Error uploading banner');
        } finally {
            setLogoUploadLoading(false);
            event.target.value = '';
        }
    };

    const handleSlideUpdate = (slideIndex, field, value) => {
        const updatedSlides = [...bannerSlides];
        updatedSlides[slideIndex][field] = value;
        setBannerSlides(updatedSlides);
        handleInputChange('banner_section', 'slides', updatedSlides);
    };

    const removeSlide = (slideIndex) => {
        const updatedSlides = bannerSlides.filter((_, index) => index !== slideIndex);
        setBannerSlides(updatedSlides);
        handleInputChange('banner_section', 'slides', updatedSlides);
    };

    const toggleCategorySelection = (categoryName) => {
        const isCurrentlySelected = selectedCategories.some(selected =>
            typeof selected === 'object' ? selected.category === categoryName : selected === categoryName
        );

        let updatedCategories;
        if (isCurrentlySelected) {
            // Remove category
            updatedCategories = selectedCategories.filter(selected =>
                typeof selected === 'object' ? selected.category !== categoryName : selected !== categoryName
            );
        } else {
            // Add category with image_url field
            updatedCategories = [...selectedCategories, { category: categoryName, image_url: '' }];
        }

        setSelectedCategories(updatedCategories);
        handleInputChange('category_section', 'selected_categories', updatedCategories);
    };


    const toggleProductSelection = (productId) => {
        const updatedProducts = featuredProducts.includes(productId)
            ? featuredProducts.filter(id => id !== productId)
            : [...featuredProducts, productId];

        setFeaturedProducts(updatedProducts);
        handleInputChange('featured_section', 'selected_products', updatedProducts);
    };

    const toggleSection = (section, enabled) => {
        handleInputChange(section, 'enabled', enabled);
    };

    const handleInputChange = (section, field, value) => {
        if (section) {
            setStoreData(prev => ({
                ...prev,
                store_settings: {
                    ...prev.store_settings,
                    [section]: {
                        ...prev.store_settings[section],
                        [field]: value
                    }
                }
            }));
        } else {
            setStoreData(prev => ({
                ...prev,
                store_settings: {
                    ...prev.store_settings,
                    [field]: value
                }
            }));
        }
    };

    const handleSaveSettings = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${process.env.REACT_APP_APIURL}business/store/settings`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    business_id: businessId,
                    ...storeData.store_settings,
                    header_color: storeData.store_settings.header_color,  // Add this
                    footer_color: storeData.store_settings.footer_color,
                    facebook: storeData.store_settings.social_links.facebook,
                    instagram: storeData.store_settings.social_links.instagram,
                    twitter: storeData.store_settings.social_links.twitter,
                    meta_title: storeData.store_settings.seo_settings.meta_title,
                    meta_description: storeData.store_settings.seo_settings.meta_description,
                    meta_keywords: storeData.store_settings.seo_settings.meta_keywords,

                    banner_section: storeData.store_settings.banner_section,
                    category_section: storeData.store_settings.category_section,
                    featured_section: storeData.store_settings.featured_section,
                    four_column_section: storeData.store_settings.four_column_section
                })
            });

            const data = await response.json();

            if (data.status === 'success') {
                toast.success('Store settings updated successfully');
            } else {
                toast.error('Failed to update store settings');
            }
        } catch (error) {
            console.error('Error updating store settings:', error);
            toast.error('Error updating store settings');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleStore = async (newStatus) => {
        try {
            setLoading(true);
            const response = await fetch(`${process.env.REACT_APP_APIURL}business/store/toggle`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    business_id: businessId,
                    store_status: newStatus
                })
            });

            const data = await response.json();

            if (data.status === 'success') {
                setStoreData(prev => ({ ...prev, store_status: newStatus }));
                toast.success(`Store ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
            } else {
                toast.error('Failed to update store status');
            }
        } catch (error) {
            console.error('Error toggling store status:', error);
            toast.error('Error updating store status');
        } finally {
            setLoading(false);
        }
    };
    const updateCategoryImage = (categoryName, imageUrl) => {
        const updatedCategories = selectedCategories.map(selected => {
            if (typeof selected === 'object' && selected.category === categoryName) {
                return { ...selected, image_url: imageUrl };
            } else if (selected === categoryName) {
                return { category: categoryName, image_url: imageUrl };
            }
            return selected;
        });

        setSelectedCategories(updatedCategories);
        handleInputChange('category_section', 'selected_categories', updatedCategories);
    };

    const handleCategoryImageUpload = async (event, categoryName) => {
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);
        formData.append('reference', 'category');
        formData.append('user_id', businessId);

        try {
            setLogoUploadLoading(true);
            const response = await fetch(`${process.env.REACT_APP_APIURL}file/event/upload`, {
                method: 'POST',
                body: formData
            });

            const data = await response.json();

            if (data.status === 'success' && data.result.files.length > 0) {
                const fileUrl = data.result.files[0].file_url;
                updateCategoryImage(categoryName, fileUrl);
                toast.success('Category image uploaded successfully');
            } else {
                toast.error('Failed to upload category image');
            }
        } catch (error) {
            console.error('Error uploading category image:', error);
            toast.error('Error uploading category image');
        } finally {
            setLogoUploadLoading(false);
            event.target.value = '';
        }
    };

    const handleColumnImageUpload = async (event, columnIndex, imageIndex) => {
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);
        formData.append('reference', 'column');
        formData.append('user_id', businessId);

        try {
            setLogoUploadLoading(true);
            const response = await fetch(`${process.env.REACT_APP_APIURL}file/event/upload`, {
                method: 'POST',
                body: formData
            });

            const data = await response.json();

            if (data.status === 'success' && data.result.files.length > 0) {
                const fileUrl = data.result.files[0].file_url;
                updateColumnImage(columnIndex, imageIndex, 'image_url', fileUrl);
                toast.success('Column image uploaded successfully');
            } else {
                toast.error('Failed to upload column image');
            }
        } catch (error) {
            console.error('Error uploading column image:', error);
            toast.error('Error uploading column image');
        } finally {
            setLogoUploadLoading(false);
            event.target.value = '';
        }
    };

    const updateColumnImage = (columnIndex, imageIndex, field, value) => {
        const updatedColumns = [...fourColumnSection];
        if (!updatedColumns[columnIndex]) {
            updatedColumns[columnIndex] = { title: '', items: [] };
        }
        if (!updatedColumns[columnIndex].items[imageIndex]) {
            updatedColumns[columnIndex].items[imageIndex] = { image_url: '', description: '', link_url: '' };
        }
        updatedColumns[columnIndex].items[imageIndex][field] = value;
        setFourColumnSection(updatedColumns);
        handleInputChange('four_column_section', 'columns', updatedColumns);
    };

    const updateColumnTitle = (columnIndex, title) => {
        const updatedColumns = [...fourColumnSection];
        if (!updatedColumns[columnIndex]) {
            updatedColumns[columnIndex] = { title: '', items: [] };
        }
        updatedColumns[columnIndex].title = title;
        setFourColumnSection(updatedColumns);
        handleInputChange('four_column_section', 'columns', updatedColumns);
    };

    const addColumn = () => {
        if (fourColumnSection.length < 8) {
            const newColumn = {
                title: '',
                items: [
                    { image_url: '', description: '', link_url: '' },
                    { image_url: '', description: '', link_url: '' },
                    { image_url: '', description: '', link_url: '' },
                    { image_url: '', description: '', link_url: '' }
                ]
            };
            const updatedColumns = [...fourColumnSection, newColumn];
            setFourColumnSection(updatedColumns);
            handleInputChange('four_column_section', 'columns', updatedColumns);
        }
    };

    const removeColumn = (columnIndex) => {
        const updatedColumns = fourColumnSection.filter((_, index) => index !== columnIndex);
        setFourColumnSection(updatedColumns);
        handleInputChange('four_column_section', 'columns', updatedColumns);
    };
    const updateCategoryData = (categoryName, field, value) => {
    const updatedCategories = selectedCategories.map(selected => {
        if (typeof selected === 'object' && selected.category === categoryName) {
            const updatedCategory = { ...selected, [field]: value };
            
            // Auto-calculate offer percentage when price or original_price changes
            if ((field === 'price' || field === 'original_price') && 
                updatedCategory.price && updatedCategory.original_price && 
                updatedCategory.original_price > updatedCategory.price) {
                updatedCategory.offer_percentage = Math.round(
                    ((updatedCategory.original_price - updatedCategory.price) / updatedCategory.original_price) * 100
                );
            } else if (field === 'price' || field === 'original_price') {
                updatedCategory.offer_percentage = 0;
            }
            
            return updatedCategory;
        } else if (selected === categoryName) {
            const newCategoryData = {
                category: categoryName,
                image_url: field === 'image_url' ? value : '',
                description: field === 'description' ? value : '',
                price: field === 'price' ? value : 0,
                original_price: field === 'original_price' ? value : 0,
                offer_percentage: field === 'offer_percentage' ? value : 0,
                offer_text: field === 'offer_text' ? value : ''
            };
            
            // Auto-calculate offer percentage
            if ((field === 'price' || field === 'original_price') && 
                newCategoryData.price && newCategoryData.original_price && 
                newCategoryData.original_price > newCategoryData.price) {
                newCategoryData.offer_percentage = Math.round(
                    ((newCategoryData.original_price - newCategoryData.price) / newCategoryData.original_price) * 100
                );
            }
            
            return newCategoryData;
        }
        return selected;
    });

    setSelectedCategories(updatedCategories);
    handleInputChange('category_section', 'selected_categories', updatedCategories);
};



    const storeUrl = `https://${storeData.store_subdomain}.eventseasy.in`;

    if (loading && !storeData.business_name) {
        return (
            <div className="content-wrapper">
                <div className="content-header">
                    <div className="container-fluid">
                        <h1 className="m-0">Loading...</h1>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="wrapper">
            <header>
                <NavigationBar />
            </header>
            <div className="content-wrapper">
                {/* Content Header */}
                <div className="content-header">
                    <div className="container-fluid">
                        <div className="row mb-2">
                            <div className="col-sm-6">
                                <h1 className="m-0">Web Store Settings</h1>
                            </div>
                            <div className="col-sm-6">
                                <div className="float-sm-right">
                                    <span className={`badge badge-lg ${storeData.store_status === 'active' ? 'badge-success' :
                                        storeData.store_status === 'inactive' ? 'badge-warning' : 'badge-danger'
                                        }`}>
                                        {storeData.store_status.toUpperCase()}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main content */}
                <section className="content">
                    <div className="container-fluid">

                        {/* Store Status Card */}
                        <div className="row">
                            <div className="col-md-6">
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">Store Status</h3>
                                    </div>
                                    <div className="card-body">
                                        <p><strong>Store URL:</strong> <a href={storeUrl} target="_blank" rel="noopener noreferrer">{storeUrl}</a></p>
                                        <p><strong>Current Status:</strong>
                                            <span className={`badge ml-2 ${storeData.store_status === 'active' ? 'badge-success' :
                                                storeData.store_status === 'inactive' ? 'badge-warning' : 'badge-danger'
                                                }`}>
                                                {storeData.store_status}
                                            </span>
                                        </p>
                                        <div className="mt-3">
                                            {storeData.store_status === 'active' ? (
                                                <button
                                                    className="btn btn-warning mr-2"
                                                    onClick={() => handleToggleStore('inactive')}
                                                    disabled={loading}
                                                >
                                                    Deactivate Store
                                                </button>
                                            ) : (
                                                <button
                                                    className="btn btn-success mr-2"
                                                    onClick={() => handleToggleStore('active')}
                                                    disabled={loading}
                                                >
                                                    Activate Store
                                                </button>
                                            )}
                                            <a href={storeUrl} target="_blank" rel="noopener noreferrer" className="btn btn-info">
                                                <i className="fas fa-external-link-alt"></i> View Store
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Store Customization */}
                        <div className="row">
                            <div className="col-md-8">
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">Store Appearance</h3>
                                    </div>
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label>Logo URL</label>
                                                    <div className="input-group">
                                                        <input
                                                            type="url"
                                                            className="form-control"
                                                            value={storeData.store_settings.logo_url}
                                                            onChange={(e) => handleInputChange(null, 'logo_url', e.target.value)}
                                                            placeholder="https://example.com/logo.png"
                                                        />
                                                        <div className="input-group-append">
                                                            <label className="btn btn-outline-secondary" htmlFor="logoUpload">
                                                                {logoUploadLoading ? (
                                                                    <i className="fas fa-spinner fa-spin"></i>
                                                                ) : (
                                                                    <i className="fas fa-upload"></i>
                                                                )}
                                                                Browse
                                                            </label>
                                                            <input
                                                                id="logoUpload"
                                                                type="file"
                                                                accept="image/*"
                                                                onChange={handleLogoUpload}
                                                                style={{ display: 'none' }}
                                                                disabled={logoUploadLoading}
                                                            />
                                                        </div>
                                                    </div>
                                                    <small className="text-muted">You can paste a URL or upload an image file</small>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label>Font Family</label>
                                                    <select
                                                        className="form-control"
                                                        value={storeData.store_settings.font_family}
                                                        onChange={(e) => handleInputChange(null, 'font_family', e.target.value)}
                                                    >
                                                        <option value="Arial, sans-serif">Arial</option>
                                                        <option value="Georgia, serif">Georgia</option>
                                                        <option value="Helvetica, sans-serif">Helvetica</option>
                                                        <option value="Times New Roman, serif">Times New Roman</option>
                                                        <option value="Verdana, sans-serif">Verdana</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-4">
                                                <div className="form-group">
                                                    <label>Primary Color</label>
                                                    <input
                                                        type="color"
                                                        className="form-control"
                                                        value={storeData.store_settings.primary_color}
                                                        onChange={(e) => handleInputChange(null, 'primary_color', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="form-group">
                                                    <label>Secondary Color</label>
                                                    <input
                                                        type="color"
                                                        className="form-control"
                                                        value={storeData.store_settings.secondary_color}
                                                        onChange={(e) => handleInputChange(null, 'secondary_color', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="form-group">
                                                    <label>Accent Color</label>
                                                    <input
                                                        type="color"
                                                        className="form-control"
                                                        value={storeData.store_settings.accent_color}
                                                        onChange={(e) => handleInputChange(null, 'accent_color', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label>Header Color</label>
                                                    <input
                                                        type="color"
                                                        className="form-control"
                                                        value={storeData.store_settings.header_color}
                                                        onChange={(e) => handleInputChange(null, 'header_color', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label>Footer Color</label>
                                                    <input
                                                        type="color"
                                                        className="form-control"
                                                        value={storeData.store_settings.footer_color}
                                                        onChange={(e) => handleInputChange(null, 'footer_color', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="form-group">
                                            <label>Store Description</label>
                                            <textarea
                                                className="form-control"
                                                rows="3"
                                                value={storeData.store_settings.store_description}
                                                onChange={(e) => handleInputChange(null, 'store_description', e.target.value)}
                                                placeholder="Describe your store..."
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-md-4">
                                {/* Preview Card */}
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">Preview</h3>
                                    </div>
                                    <div className="card-body text-center" style={{
                                        backgroundColor: storeData.store_settings.secondary_color,
                                        fontFamily: storeData.store_settings.font_family
                                    }}>
                                        {/* Header preview */}
                                        <div style={{
                                            backgroundColor: storeData.store_settings.header_color,
                                            padding: '10px',
                                            marginBottom: '10px',
                                            borderRadius: '4px'
                                        }}>
                                            <small style={{ color: '#666' }}>Header</small>
                                        </div>

                                        {storeData.store_settings.logo_url && (
                                            <img
                                                src={storeData.store_settings.logo_url}
                                                alt="Store Logo"
                                                style={{ maxWidth: '100px', maxHeight: '50px' }}
                                                className="mb-2"
                                            />
                                        )}
                                        <h4 style={{ color: storeData.store_settings.primary_color }}>
                                            {storeData.business_name}
                                        </h4>
                                        <p style={{ color: storeData.store_settings.primary_color }}>
                                            {storeData.store_settings.store_description || "Your store description will appear here"}
                                        </p>
                                        <button
                                            className="btn btn-sm"
                                            style={{
                                                backgroundColor: storeData.store_settings.accent_color,
                                                color: 'white',
                                                fontFamily: storeData.store_settings.font_family
                                            }}
                                        >
                                            Sample Button
                                        </button>

                                        {/* Footer preview */}
                                        <div style={{
                                            backgroundColor: storeData.store_settings.footer_color,
                                            padding: '10px',
                                            marginTop: '10px',
                                            borderRadius: '4px'
                                        }}>
                                            <small style={{ color: '#666' }}>Footer</small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Contact Information */}
                        <div className="row">
                            <div className="col-md-6">
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">Contact Information</h3>
                                    </div>
                                    <div className="card-body">
                                        <div className="form-group">
                                            <label>Contact Email</label>
                                            <input
                                                type="email"
                                                className="form-control"
                                                value={storeData.store_settings.contact_email}
                                                onChange={(e) => handleInputChange(null, 'contact_email', e.target.value)}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Contact Phone</label>
                                            <input
                                                type="tel"
                                                className="form-control"
                                                value={storeData.store_settings.contact_phone}
                                                onChange={(e) => handleInputChange(null, 'contact_phone', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">Social Media Links</h3>
                                    </div>
                                    <div className="card-body">
                                        <div className="form-group">
                                            <label>Facebook</label>
                                            <input
                                                type="url"
                                                className="form-control"
                                                value={storeData.store_settings.social_links.facebook}
                                                onChange={(e) => handleInputChange('social_links', 'facebook', e.target.value)}
                                                placeholder="https://facebook.com/yourpage"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Instagram</label>
                                            <input
                                                type="url"
                                                className="form-control"
                                                value={storeData.store_settings.social_links.instagram}
                                                onChange={(e) => handleInputChange('social_links', 'instagram', e.target.value)}
                                                placeholder="https://instagram.com/yourhandle"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Twitter</label>
                                            <input
                                                type="url"
                                                className="form-control"
                                                value={storeData.store_settings.social_links.twitter}
                                                onChange={(e) => handleInputChange('social_links', 'twitter', e.target.value)}
                                                placeholder="https://twitter.com/yourhandle"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* SEO Settings */}
                        <div className="row">
                            <div className="col-md-12">
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">SEO Settings</h3>
                                    </div>
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label>Meta Title</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={storeData.store_settings.seo_settings.meta_title}
                                                        onChange={(e) => handleInputChange('seo_settings', 'meta_title', e.target.value)}
                                                    />
                                                </div>
                                                <div className="form-group">
                                                    <label>Meta Description</label>
                                                    <textarea
                                                        className="form-control"
                                                        rows="3"
                                                        value={storeData.store_settings.seo_settings.meta_description}
                                                        onChange={(e) => handleInputChange('seo_settings', 'meta_description', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label>Meta Keywords</label>
                                                    <textarea
                                                        className="form-control"
                                                        rows="5"
                                                        value={storeData.store_settings.seo_settings.meta_keywords}
                                                        onChange={(e) => handleInputChange('seo_settings', 'meta_keywords', e.target.value)}
                                                        placeholder="keyword1, keyword2, keyword3"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-12">
                                <div className="card">
                                    <div className="card-header d-flex justify-content-between align-items-center">
                                        <h3 className="card-title">Banner Slider Section</h3>
                                        <div className="custom-control custom-switch">
                                            <input
                                                type="checkbox"
                                                className="custom-control-input"
                                                id="bannerSectionSwitch"
                                                checked={storeData.store_settings.banner_section.enabled}
                                                onChange={(e) => toggleSection('banner_section', e.target.checked)}
                                            />
                                            <label className="custom-control-label" htmlFor="bannerSectionSwitch">Enable</label>
                                        </div>
                                    </div>
                                    {storeData.store_settings.banner_section.enabled && (
                                        <div className="card-body">
                                            <div className="mb-3">
                                                <label className="btn btn-success">
                                                    <i className="fas fa-plus"></i> Add New Slide
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={(e) => handleBannerUpload(e)}
                                                        style={{ display: 'none' }}
                                                    />
                                                </label>
                                            </div>

                                            {bannerSlides.map((slide, index) => (
                                                <div key={slide.id || index} className="card mb-3">
                                                    <div className="card-body">
                                                        <div className="row">
                                                            <div className="col-md-4">
                                                                {slide.image_url && (
                                                                    <img
                                                                        src={slide.image_url}
                                                                        alt={`Slide ${index + 1}`}
                                                                        className="img-fluid mb-2"
                                                                        style={{ maxHeight: '150px' }}
                                                                    />
                                                                )}
                                                                <label className="btn btn-outline-primary btn-sm">
                                                                    Change Image
                                                                    <input
                                                                        type="file"
                                                                        accept="image/*"
                                                                        onChange={(e) => handleBannerUpload(e, index)}
                                                                        style={{ display: 'none' }}
                                                                    />
                                                                </label>
                                                            </div>
                                                            <div className="col-md-8">
                                                                <div className="form-group">
                                                                    <label>Title</label>
                                                                    <input
                                                                        type="text"
                                                                        className="form-control"
                                                                        value={slide.title || ''}
                                                                        onChange={(e) => handleSlideUpdate(index, 'title', e.target.value)}
                                                                    />
                                                                </div>
                                                                <div className="form-group">
                                                                    <label>Subtitle</label>
                                                                    <input
                                                                        type="text"
                                                                        className="form-control"
                                                                        value={slide.subtitle || ''}
                                                                        onChange={(e) => handleSlideUpdate(index, 'subtitle', e.target.value)}
                                                                    />
                                                                </div>
                                                                <div className="row">
                                                                    <div className="col-md-6">
                                                                        <div className="form-group">
                                                                            <label>Button Text</label>
                                                                            <input
                                                                                type="text"
                                                                                className="form-control"
                                                                                value={slide.button_text || ''}
                                                                                onChange={(e) => handleSlideUpdate(index, 'button_text', e.target.value)}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-md-6">
                                                                        <div className="form-group">
                                                                            <label>Button Link</label>
                                                                            <input
                                                                                type="url"
                                                                                className="form-control"
                                                                                value={slide.button_link || ''}
                                                                                onChange={(e) => handleSlideUpdate(index, 'button_link', e.target.value)}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <button
                                                                    className="btn btn-danger btn-sm"
                                                                    onClick={() => removeSlide(index)}
                                                                >
                                                                    Remove Slide
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Category Display Section */}
                        <div className="row">
                            <div className="col-md-12">
                                <div className="card">
                                    <div className="card-header d-flex justify-content-between align-items-center">
                                        <h3 className="card-title">Category Display Section</h3>
                                        <div className="custom-control custom-switch">
                                            <input
                                                type="checkbox"
                                                className="custom-control-input"
                                                id="categorySectionSwitch"
                                                checked={storeData.store_settings.category_section.enabled}
                                                onChange={(e) => toggleSection('category_section', e.target.checked)}
                                            />
                                            <label className="custom-control-label" htmlFor="categorySectionSwitch">Enable</label>
                                        </div>
                                    </div>
                                    {storeData.store_settings.category_section.enabled && (
                                        <div className="card-body">
                                            <div className="form-group">
                                                <label>Section Title</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={storeData.store_settings.category_section.title}
                                                    onChange={(e) => handleInputChange('category_section', 'title', e.target.value)}
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label>Select Categories to Display</label>
                                                <div className="row">
                                                    {availableCategories.map((category) => {
                                                        const isSelected = selectedCategories.some(selected =>
                                                            typeof selected === 'object' ? selected.category === category.category : selected === category.category
                                                        );
                                                        const selectedCategoryData = selectedCategories.find(selected =>
                                                            typeof selected === 'object' ? selected.category === category.category : selected === category.category
                                                        );

                                                        return (
                                                            <div key={category.category} className="col-md-6 mb-4">
                                                                <div className="card" style={{ border: isSelected ? '2px solid #007bff' : '1px solid #dee2e6' }}>
                                                                    <div className="card-body">
                                                                        <div className="custom-control custom-checkbox mb-3">
                                                                            <input
                                                                                type="checkbox"
                                                                                className="custom-control-input"
                                                                                id={`category-${category.category}`}
                                                                                checked={isSelected}
                                                                                onChange={() => toggleCategorySelection(category.category)}
                                                                            />
                                                                            <label
                                                                                className="custom-control-label fw-bold"
                                                                                htmlFor={`category-${category.category}`}
                                                                            >
                                                                                {category.category}
                                                                            </label>
                                                                        </div>

                                                                        {isSelected && (
                                                                            <div className="category-config-section">
                                                                                {/* Image Section */}
                                                                                <div className="mb-3">
                                                                                    <label className="form-label">Category Image</label>
                                                                                    {selectedCategoryData?.image_url && (
                                                                                        <div className="mb-2">
                                                                                            <img
                                                                                                src={typeof selectedCategoryData === 'object' ? selectedCategoryData.image_url : ''}
                                                                                                alt={category.category}
                                                                                                style={{
                                                                                                    width: '100%',
                                                                                                    height: '120px',
                                                                                                    objectFit: 'cover',
                                                                                                    borderRadius: '8px',
                                                                                                    border: '1px solid #dee2e6'
                                                                                                }}
                                                                                            />
                                                                                        </div>
                                                                                    )}
                                                                                    <div className="input-group mb-2">
                                                                                        <input
                                                                                            type="url"
                                                                                            className="form-control form-control-sm"
                                                                                            placeholder="Enter image URL"
                                                                                            value={typeof selectedCategoryData === 'object' ? selectedCategoryData.image_url || '' : ''}
                                                                                            onChange={(e) => updateCategoryData(category.category, 'image_url', e.target.value)}
                                                                                        />
                                                                                    </div>
                                                                                    <label className="btn btn-outline-primary btn-sm w-100">
                                                                                        {logoUploadLoading ? (
                                                                                            <>
                                                                                                <i className="fas fa-spinner fa-spin"></i> Uploading...
                                                                                            </>
                                                                                        ) : (
                                                                                            <>
                                                                                                <i className="fas fa-upload"></i> Upload Image
                                                                                            </>
                                                                                        )}
                                                                                        <input
                                                                                            type="file"
                                                                                            accept="image/*"
                                                                                            onChange={(e) => handleCategoryImageUpload(e, category.category)}
                                                                                            style={{ display: 'none' }}
                                                                                            disabled={logoUploadLoading}
                                                                                        />
                                                                                    </label>
                                                                                </div>

                                                                                {/* Description */}
                                                                                <div className="form-group">
                                                                                    <label>Description</label>
                                                                                    <textarea
                                                                                        className="form-control form-control-sm"
                                                                                        rows="2"
                                                                                        placeholder="Enter category description"
                                                                                        value={typeof selectedCategoryData === 'object' ? selectedCategoryData.description || '' : ''}
                                                                                        onChange={(e) => updateCategoryData(category.category, 'description', e.target.value)}
                                                                                    />
                                                                                </div>

                                                                                {/* Price Section */}
                                                                                <div className="row">
                                                                                    <div className="col-6">
                                                                                        <div className="form-group">
                                                                                            <label>Current Price (₹)</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                className="form-control form-control-sm"
                                                                                                placeholder="0"
                                                                                                min="0"
                                                                                                value={typeof selectedCategoryData === 'object' ? selectedCategoryData.price || '' : ''}
                                                                                                onChange={(e) => updateCategoryData(category.category, 'price', parseInt(e.target.value) || 0)}
                                                                                            />
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="col-6">
                                                                                        <div className="form-group">
                                                                                            <label>Original Price (₹)</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                className="form-control form-control-sm"
                                                                                                placeholder="0"
                                                                                                min="0"
                                                                                                value={typeof selectedCategoryData === 'object' ? selectedCategoryData.original_price || '' : ''}
                                                                                                onChange={(e) => updateCategoryData(category.category, 'original_price', parseInt(e.target.value) || 0)}
                                                                                            />
                                                                                        </div>
                                                                                    </div>
                                                                                </div>

                                                                                {/* Offer Section */}
                                                                                <div className="row">
                                                                                    <div className="col-6">
                                                                                        <div className="form-group">
                                                                                            <label>Offer % (Auto-calculated)</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                className="form-control form-control-sm"
                                                                                                placeholder="0"
                                                                                                value={
                                                                                                    typeof selectedCategoryData === 'object' &&
                                                                                                        selectedCategoryData.price &&
                                                                                                        selectedCategoryData.original_price &&
                                                                                                        selectedCategoryData.original_price > selectedCategoryData.price
                                                                                                        ? Math.round(((selectedCategoryData.original_price - selectedCategoryData.price) / selectedCategoryData.original_price) * 100)
                                                                                                        : selectedCategoryData?.offer_percentage || ''
                                                                                                }
                                                                                                onChange={(e) => updateCategoryData(category.category, 'offer_percentage', parseInt(e.target.value) || 0)}
                                                                                            />
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="col-6">
                                                                                        <div className="form-group">
                                                                                            <label>Offer Text</label>
                                                                                            <input
                                                                                                type="text"
                                                                                                className="form-control form-control-sm"
                                                                                                placeholder="e.g., LIMITED TIME"
                                                                                                value={typeof selectedCategoryData === 'object' ? selectedCategoryData.offer_text || '' : ''}
                                                                                                onChange={(e) => updateCategoryData(category.category, 'offer_text', e.target.value)}
                                                                                            />
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Featured Products Section */}
                        <div className="row">
                            <div className="col-md-12">
                                <div className="card">
                                    <div className="card-header d-flex justify-content-between align-items-center">
                                        <h3 className="card-title">Featured Products Section</h3>
                                        <div className="custom-control custom-switch">
                                            <input
                                                type="checkbox"
                                                className="custom-control-input"
                                                id="featuredSectionSwitch"
                                                checked={storeData.store_settings.featured_section.enabled}
                                                onChange={(e) => toggleSection('featured_section', e.target.checked)}
                                            />
                                            <label className="custom-control-label" htmlFor="featuredSectionSwitch">Enable</label>
                                        </div>
                                    </div>
                                    {storeData.store_settings.featured_section.enabled && (
                                        <div className="card-body">
                                            <div className="form-group">
                                                <label>Section Title</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={storeData.store_settings.featured_section.title}
                                                    onChange={(e) => handleInputChange('featured_section', 'title', e.target.value)}
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label>Select Products to Feature (Max 8)</label>
                                                {availableProducts.length === 0 ? (
                                                    <div className="alert alert-warning text-center" role="alert">
                                                        <i className="fas fa-exclamation-triangle mb-2"></i>
                                                        <h5>No Products Found</h5>
                                                        <p className="mb-0">Please upload products first to feature them in this section.</p>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <div className="row" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                                            {availableProducts.map((product) => (
                                                                <div key={product._id} className="col-md-3 mb-3">
                                                                    <div className="card">
                                                                        <img
                                                                            src={product.url || product.url}
                                                                            className="card-img-top"
                                                                            style={{ height: '120px', objectFit: 'cover' }}
                                                                            alt={product.name}
                                                                        />
                                                                        <div className="card-body p-2">
                                                                            <h6 className="card-title" style={{ fontSize: '12px' }}>
                                                                                {product.name}
                                                                            </h6>
                                                                        </div>
                                                                        <div className="custom-control custom-checkbox mx-2">
                                                                            <input
                                                                                type="checkbox"
                                                                                className="custom-control-input"
                                                                                id={`product-${product._id}`}
                                                                                checked={featuredProducts.includes(product._id)}
                                                                                onChange={() => toggleProductSelection(product._id)}
                                                                                disabled={!featuredProducts.includes(product._id) && featuredProducts.length >= 8}
                                                                            />
                                                                            <label
                                                                                className="custom-control-label"
                                                                                htmlFor={`product-${product._id}`}
                                                                            >
                                                                                Feature
                                                                            </label>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                        <small className="text-muted">
                                                            Selected: {featuredProducts.length}/8 products
                                                        </small>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-md-12">
                                <div className="card">
                                    <div className="card-header d-flex justify-content-between align-items-center">
                                        <h3 className="card-title">Four Column Display Section</h3>
                                        <div className="custom-control custom-switch">
                                            <input
                                                type="checkbox"
                                                className="custom-control-input"
                                                id="fourColumnSectionSwitch"
                                                checked={storeData.store_settings.four_column_section?.enabled || false}
                                                onChange={(e) => toggleSection('four_column_section', e.target.checked)}
                                            />
                                            <label className="custom-control-label" htmlFor="fourColumnSectionSwitch">Enable</label>
                                        </div>
                                    </div>
                                    {storeData.store_settings.four_column_section?.enabled && (
                                        <div className="card-body">
                                            <div className="form-group">
                                                <label>Section Title</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={storeData.store_settings.four_column_section?.title || ''}
                                                    onChange={(e) => handleInputChange('four_column_section', 'title', e.target.value)}
                                                    placeholder="e.g., Shop by Category"
                                                />
                                            </div>

                                            <div className="mb-3">
                                                <button
                                                    className="btn btn-success"
                                                    onClick={addColumn}
                                                    disabled={fourColumnSection.length >= 8}
                                                >
                                                    <i className="fas fa-plus"></i> Add Column (Max 8)
                                                </button>
                                            </div>

                                            <div className="row">
                                                {fourColumnSection.map((column, columnIndex) => (
                                                    <div key={columnIndex} className="col-md-3 mb-4">
                                                        <div className="card border-primary">
                                                            <div className="card-header d-flex justify-content-between align-items-center">
                                                                <h6 className="mb-0">Column {columnIndex + 1}</h6>
                                                                <button
                                                                    className="btn btn-danger btn-sm"
                                                                    onClick={() => removeColumn(columnIndex)}
                                                                >
                                                                    <i className="fas fa-trash"></i>
                                                                </button>
                                                            </div>
                                                            <div className="card-body">
                                                                {/* Column Title */}
                                                                <div className="form-group">
                                                                    <label>Column Title</label>
                                                                    <input
                                                                        type="text"
                                                                        className="form-control"
                                                                        value={column.title || ''}
                                                                        onChange={(e) => updateColumnTitle(columnIndex, e.target.value)}
                                                                        placeholder="e.g., TVs & Electronics"
                                                                    />
                                                                </div>

                                                                {/* Four Images for this column */}
                                                                {[0, 1, 2, 3].map((imageIndex) => (
                                                                    <div key={imageIndex} className="mb-3 p-2 border rounded">
                                                                        <label className="form-label">Item {imageIndex + 1}</label>

                                                                        {/* Image Preview */}
                                                                        {column.items?.[imageIndex]?.image_url && (
                                                                            <div className="mb-2">
                                                                                <img
                                                                                    src={column.items[imageIndex].image_url}
                                                                                    alt={`Column ${columnIndex + 1} Item ${imageIndex + 1}`}
                                                                                    style={{
                                                                                        width: '100%',
                                                                                        height: '80px',
                                                                                        objectFit: 'cover',
                                                                                        borderRadius: '4px'
                                                                                    }}
                                                                                />
                                                                            </div>
                                                                        )}

                                                                        {/* Image URL Input */}
                                                                        <div className="form-group">
                                                                            <input
                                                                                type="url"
                                                                                className="form-control form-control-sm"
                                                                                placeholder="Image URL"
                                                                                value={column.items?.[imageIndex]?.image_url || ''}
                                                                                onChange={(e) => updateColumnImage(columnIndex, imageIndex, 'image_url', e.target.value)}
                                                                            />
                                                                        </div>

                                                                        {/* Image Upload Button */}
                                                                        <label className="btn btn-outline-primary btn-sm w-100 mb-2">
                                                                            <i className="fas fa-upload"></i> Upload
                                                                            <input
                                                                                type="file"
                                                                                accept="image/*"
                                                                                onChange={(e) => handleColumnImageUpload(e, columnIndex, imageIndex)}
                                                                                style={{ display: 'none' }}
                                                                                disabled={logoUploadLoading}
                                                                            />
                                                                        </label>

                                                                        {/* Description Input */}
                                                                        <div className="form-group">
                                                                            <input
                                                                                type="text"
                                                                                className="form-control form-control-sm"
                                                                                placeholder="Description"
                                                                                value={column.items?.[imageIndex]?.description || ''}
                                                                                onChange={(e) => updateColumnImage(columnIndex, imageIndex, 'description', e.target.value)}
                                                                            />
                                                                        </div>

                                                                        {/* Link URL Input */}
                                                                        <div className="form-group">
                                                                            <input
                                                                                type="url"
                                                                                className="form-control form-control-sm"
                                                                                placeholder="Link URL (optional)"
                                                                                value={column.items?.[imageIndex]?.link_url || ''}
                                                                                onChange={(e) => updateColumnImage(columnIndex, imageIndex, 'link_url', e.target.value)}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Save Button */}
                        <div className="row">
                            <div className="col-md-12">
                                <button
                                    className="btn btn-primary btn-lg"
                                    onClick={handleSaveSettings}
                                    disabled={loading}
                                >
                                    {loading ? 'Saving...' : 'Save Settings'}
                                </button>
                            </div>
                        </div>

                    </div>
                </section>
            </div>
            <Footer />
        </div>

    );
};

export default WebStoreSettings;